import { useState } from "react";
import { useLantern, useSession } from "../hooks";
import { dayName, describeQuiet, quietUntil } from "../quiet";
import { browserZone, timeZones } from "../localtime";
import type { QuietHours } from "../sync/types";
import { Badge, Card, Field, Switch } from "./ui";
import { t, tc } from "../i18n";

const here = browserZone();

// QuietHoursCard sets the daily times notifications pause on their own, on
// every device: nights, say, or weekends.
export function QuietHoursCard() {
  const session = useSession();
  const state = useLantern();
  const saved = state.quietHours;
  const [q, setQ] = useState<QuietHours>(saved ?? { start: "22:00", end: "08:00", days: [0, 1, 2, 3, 4, 5, 6], time_zone: here });
  const [error, setError] = useState("");
  const [done, setDone] = useState("");
  const changed = !saved || JSON.stringify({ ...q, days: [...q.days].sort() }) !== JSON.stringify(saved);

  async function run(next: QuietHours | null, said: string) {
    setError("");
    setDone("");
    try {
      await session.setQuietHours(next);
      setDone(said);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }
  const toggleDay = (d: number) => setQ((x) => ({ ...x, days: x.days.includes(d) ? x.days.filter((y) => y !== d) : [...x.days, d] }));
  const now = quietUntil(saved);

  return (
    <Card
      title={t("Quiet hours")}
      description={t("Pause notifications every day at set times, on all your devices. Messages still arrive; you see them when you look.")}
      actions={saved ? <Badge tone={now ? "accent" : "neutral"}>{now ? t("Quiet now") : describeQuiet(saved)}</Badge> : undefined}
    >
      <Switch
        checked={!!saved}
        onChange={(on) => void (on ? run(q, t("Quiet hours are on.")) : run(null, t("Quiet hours are off.")))}
        label={t("Pause notifications at set times")}
        description={
          saved
            ? t("{when}, {zone} time.", { when: describeQuiet(saved), zone: saved.time_zone.replace(/_/g, " ") })
            : t("For example at night, so your phone and laptop stay silent.")
        }
      />
      <div className="quiet-fields">
        <div className="inline-fields">
          <Field label={tc("time", "From")}>
            <input type="time" value={q.start} onChange={(e) => setQ({ ...q, start: e.target.value })} required />
          </Field>
          <Field label={t("Until")}>
            <input type="time" value={q.end} onChange={(e) => setQ({ ...q, end: e.target.value })} required />
          </Field>
        </div>
        <Field label={tc("days", "On")} hint={t("The days they start; quiet hours past midnight run into the next morning.")}>
          <div className="day-chips" role="group" aria-label={t("Days")}>
            {[0, 1, 2, 3, 4, 5, 6].map((d) => (
              <button key={d} type="button" className={q.days.includes(d) ? "on" : ""} aria-pressed={q.days.includes(d)} onClick={() => toggleDay(d)}>
                {dayName(d)}
              </button>
            ))}
          </div>
        </Field>
        <Field label={t("Time zone")}>
          <select value={q.time_zone} onChange={(e) => setQ({ ...q, time_zone: e.target.value })}>
            {timeZones(q.time_zone).map((z) => (
              <option key={z} value={z}>
                {z.replace(/_/g, " ")}
              </option>
            ))}
          </select>
        </Field>
        {error && <p className="error">{error}</p>}
        {done && <p className="notice">{done}</p>}
        <div className="form-actions">
          <button type="button" disabled={!changed || q.days.length === 0 || q.start === q.end} onClick={() => void run(q, saved ? t("Saved.") : t("Quiet hours are on."))}>
            {saved ? t("Save") : t("Turn on")}
          </button>
        </div>
      </div>
    </Card>
  );
}
